import Badge from '@shared/ui/Badge/Badge';
import { useAppDispatch } from '@store/hooks';
import { addModal, removeModal } from '@store/reducers/appSlice';
import classNames from 'classnames';
import { t } from 'i18next';
import { type FC, memo, type PropsWithChildren, type ReactNode, useEffect } from 'react';
import ReactDOM from 'react-dom';

interface DialogProps extends PropsWithChildren {
  id: string;
  title: ReactNode;
  loading: boolean;
  loadingText?: string;
  responsive?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  onClickOutside: () => void;
  visible: boolean;
  dark?: boolean;
  classNames?: string | object;
  hideCloseButton?: boolean;
}

const Dialog: FC<DialogProps> = ({
  id,
  children,
  title,
  loading,
  loadingText,
  visible,
  dark = false,
  responsive = false,
  fullWidth = false,
  fullHeight = false,
  onClickOutside,
  hideCloseButton = false,
}) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    // look for id = 'modal-root' in the document inside body
    // if not exist create one
    let modalRoot = document.getElementById('modal-root');
    if (!modalRoot) {
      modalRoot = document.createElement('div');
      modalRoot.id = 'modal-root';
      document.body.appendChild(modalRoot);
    }
    // Keep the modal root for the lifetime of the app (do not remove on unmount)
    // This prevents layout thrashing when multiple dialogs mount/unmount.
    return () => {};
    //
  }, []);

  // Close modal on 'Escape' key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClickOutside();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClickOutside]);

  useEffect(() => {
    const dialog = document.getElementById(id) as HTMLDialogElement;
    if (visible) {
      dialog?.addEventListener('close', onClickOutside);
      dispatch(addModal(id));
    } else {
      dispatch(removeModal(id));
    }

    return () => {
      dialog?.removeEventListener('close', onClickOutside);
    };
  }, [id, onClickOutside, visible, dispatch]);

  if (!visible) {
    // Prevent rendering if the modal is not open
    return null;
  }

  const dialogContent = (
    <div
      id={id}
      className={classNames(
        'fixed modal z-9999 bg-base-300/20 backdrop-blur-xl shadow-xl',
        classNames,
        {
          'modal-open': visible,
        },
      )}
    >
      <div
        className={classNames('modal-box flex flex-col p-0 h-m', {
          'bg-base-200': dark,
          'bg-base-100': !dark,
          'w-11/12 max-w-6xl': !responsive && !fullWidth,
          'w-full max-w-full': fullWidth,
          'h-full min-h-screen': fullHeight,
        })}
      >
        <h3 className="sticky top-0 left-0 bg-linear-to-br from-base-200/95 to-base-100/95 backdrop-blur-2xl z-10 font-bold text-2xl mb-6 flex items-center gap-3 p-6 w-full border-b border-primary/20">
          <span>{title}</span>
          {loading && (
            <>
              <span className="loading loading-spinner text-primary" />
              {loadingText && <Badge color="primary">{t('tips.loading-data')}</Badge>}
            </>
          )}
          {!hideCloseButton && (
            <form className="ml-auto" method="dialog" onSubmit={() => onClickOutside()}>
              <button className="btn btn-sm btn-circle absolute z-20 right-6 top-6 text-gray-500">
                ✕
              </button>
            </form>
          )}
        </h3>
        <div className="px-6 pb-8 h-full">{children}</div>
      </div>
      {!hideCloseButton && (
        <form method="dialog" className="modal-backdrop" onSubmit={() => onClickOutside()}>
          <button>close</button>
        </form>
      )}
    </div>
  );

  // Render modal content to the #modal-root
  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return null;
  return ReactDOM.createPortal(dialogContent, modalRoot);
};

export default memo(Dialog);
